import React, { useEffect, useMemo, useState } from 'react';
import CardContainer from 'components/Tvendo/Common/CardContainer';
import FilterCustom from 'components/Tvendo/Common/FilterCustom';
import ReactTable from 'components/ReactTable/ReactTableV2';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';
import { wsFindBy, wsPost } from 'services/WService';
import RegularButton from 'components/CustomButtons/Button';
import { Add } from '@material-ui/icons';
import { Toasty, useToasty } from 'hooks/Toasty';
import { FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { WithApp } from 'contexts/appContext';
import Badge from 'components/Badge/Badge';
import { nDecimal } from 'hooks/Pipe';
import SireModal from './SireModal';
import SireMissingModal from './SireMissingModal';
import { AllowedGuard } from '../UserAction';
import ButtonDelete from 'hooks/ButtonDelete';
import { useModal } from 'hooks/useModal';

const SAP_FILTROS = [
  { value: 'T', label: 'Todos' },
  { value: 'Y', label: 'Sí están en SAP' },
  { value: 'N', label: 'No están en SAP' },
  { value: 'P', label: 'Pendientes' },
];

const columns = [
  { Header: '#', accessor: 'num', excelEnabled: false, width: 100 },
  {
    Header: 'Existe SAP',
    accessor: 'exists_sap',
    width: 110,
    excelEnabled: false,
  },
  { Header: 'Existe SAP', accessor: 'exists_sap_string', width: 110, hidden: true },
  { Header: 'Tipo', accessor: 'tipo', width: 110 },
  { Header: 'Serie', accessor: 'serie', width: 90 },
  { Header: 'Número', accessor: 'number', width: 110 },
  { Header: 'RUC / DNI', accessor: 'ruc', width: 130 },
  { Header: 'Código', accessor: 'card_code', width: 110 },
  { Header: 'Razón Social', accessor: 'partner_name' },
  { Header: 'Emisión', accessor: 'doc_date', width: 120 },
  { Header: 'Total', accessor: 'total', width: 130 },
  { Header: '', accessor: 'actions', excelEnabled: false, width: 0 },
];

function SireList(props) {
  //permission: importar (botón +), updatePermission: conciliar, deletePermission: eliminar
  const { entity, title, permission, updatePermission, deletePermission, excelName } = props;
  const [notify, showNotify] = useToasty();

  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [conciliando, setConciliando] = useState(false);
  const [sapFilter, setSapFilter] = useState('T');
  const [faltantes, setFaltantes] = useModal();

  const visibles = useMemo(() => {
    if (sapFilter === 'T') return records;
    if (sapFilter === 'P') return records.filter((r) => r.exists_sap == null);
    return records.filter((r) => r.exists_sap === sapFilter);
  }, [records, sapFilter]);

  async function listRecords() {
    setDataLoading(true);
    const res = await wsFindBy(entity, filter);
    setDataLoading(false);
    if (res.success) setRecords(res.data);
  }

  async function conciliar() {
    setConciliando(true);
    const res = await wsPost(`${entity}/conciliar`, {
      start_date: filter.start_date,
      end_date: filter.end_date,
    });
    setConciliando(false);
    if (res.success) {
      showNotify(res.message || 'Conciliación realizada', 'success');
      listRecords();
    } else {
      showNotify(res.message || 'Error al conciliar', 'error');
    }
  }

  useEffect(() => {
    if (Object.keys(filter).length === 0) return;
    listRecords();
  }, [filter]);

  return (
    <CardContainer
      title={(<>{title} {dataLoading && <LoadingSpinner />}</>)}
      color="success"
      permission={permission}
      icon={<Add />}
      onClick={() => setOpen(true)}
    >
      <Toasty action={notify} />
      <SireModal
        open={open}
        onClose={() => setOpen(false)}
        entity={entity}
        title={`Importar ${title}`}
        showNotify={showNotify}
        reload={listRecords}
      />
      <SireMissingModal
        model={faltantes}
        setOpen={setFaltantes}
        entity={entity}
        title={`${title} - SAP no declarado`}
        filter={filter}
      />
      <FilterCustom dateRange diffRange={7} getFilter={setFilter} />
      <AllowedGuard permission={updatePermission}>
        <RegularButton color="primary" disabled={conciliando || records.length === 0} onClick={conciliar}>
          {conciliando ? 'Conciliando...' : 'Conciliar con SAP'}
        </RegularButton>
      </AllowedGuard>

      <RegularButton color="info" style={{ marginLeft: 10 }} onClick={() => setFaltantes({})}>
        SAP no declarado
      </RegularButton>

      <FormControl style={{ minWidth: 180, marginLeft: 20, verticalAlign: 'bottom' }}>
        <InputLabel shrink>Existe en SAP</InputLabel>
        <Select value={sapFilter} onChange={(e) => setSapFilter(e.target.value)}>
          {SAP_FILTROS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {sapFilter !== 'T' && (
        <span style={{ marginLeft: 12, verticalAlign: 'bottom', lineHeight: '48px' }}>
          {visibles.length} de {records.length}
        </span>
      )}
      <ReactTable
        excelName={excelName}
        formatDate="DD-MM-YYYY"
        defaultWidth={1700}
        columns={columns}
        data={visibles.map((item, i) => {
          return {
          num: (
            <div className="actions-right">
              {i + 1}
              <AllowedGuard permission={deletePermission}>
                <ButtonDelete entity={entity} id={item.id} reload={() => listRecords()} />
              </AllowedGuard>
            </div>
          ),
          exists_sap:
            item.exists_sap == null ? (
              <Badge color="warning">Pendiente</Badge>
            ) : (
              <Badge color={item.exists_sap === 'Y' ? 'success' : 'danger'}>
                {item.exists_sap === 'Y' ? 'Sí' : 'No'}
              </Badge>
            ),
          exists_sap_string:
            item.exists_sap == null ? 'Pendiente' : item.exists_sap === 'Y' ? 'Sí' : 'No',
          tipo:
            { '01': 'Factura', '02': 'Recibo Hon.', '03': 'Boleta', '07': 'N. Crédito', '08': 'N. Débito' }[
              item.document_type
            ] || item.document_type,
          serie: item.serie,
          number: item.number,
          ruc: item.partner ? item.partner.LicTradNum : '-',
          partner_name: item.partner ? item.partner.CardName : '-',
          card_code: item.partner ? item.partner.CardCode : '-',
          doc_date: item.doc_date,
          total: nDecimal(item.total),
          actions: '',
        };
      })}
    />
    </CardContainer>
  );
}

export default WithApp(SireList);
